import { useTranslations } from 'next-intl';
import { createTaskByTool, createTaskByToolV2 } from '@/app/apis';
import { useToast } from '@/app/components/ui/use-toast';
import {
  type ReportEventParams,
  useCredits,
  useDailySecondTaskAlertHandler,
  useFetch,
  usePWACount,
  useTaskSubmitError,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo, TaskParams } from '@/app/types';
import { getDefaultReportParams, reportEvent } from '@/app/utils';
import { getFetchErrorMsg, saveOneClickMvTaskScene } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';

interface ToolSubmitOptions {
  taskType: TaskParams['type'];
  isFree: boolean;
  useV2ToolTaskApi?: boolean;
  /**
   * 提交成功回调
   * @returns 返回 'suppressDefaultToast' 时不显示默认 toast，否则显示默认 toast
   */
  onSubmitSuccess?: (task: TaskInfo) => 'suppressDefaultToast' | undefined;
  onCloseDialog?: () => void;
  // 用于埋点上报
  getReportParams?: (isNewbieTask?: boolean) => ReportEventParams;
  /**
   * 当检测到 ImagePromoCreationShortage 错误时的回调，用于限制数量为1
   */
  onImagePromoCreationShortage?: () => void;
}

/**
 * 处理工具类任务的通用提交逻辑，包括提交失败后出错误弹窗，提交成功后处理积分更新、上报事件、添加新任务、统计生成次数达到一定量后弹订阅弹窗等逻辑
 */
export const useToolSubmit = ({
  taskType,
  isFree,
  useV2ToolTaskApi = false,
  onSubmitSuccess,
  getReportParams,
  onCloseDialog,
  onImagePromoCreationShortage,
}: ToolSubmitOptions) => {
  const { updateCredits } = useCredits();
  const t = useTranslations();
  const { toast } = useToast();
  const pathname = usePathname();

  const addNewTask = useStore((state) => state.addNewTask);

  // 错误弹窗
  const { handleError, handleDialogOpenChange } = useTaskSubmitError({
    isFree,
    templateScene: '',
    taskType,
    onCloseDialog,
    onImagePromoCreationShortage,
  });

  // 免费用户「当日第二次发送任务」弹订阅弹窗（低积分优先，含曝光埋点，逻辑收敛在 handler 内）
  const { recordTask } = useDailySecondTaskAlertHandler();
  const { incrementPWAInstallCount } = usePWACount();
  // 接口：提交任务
  const { run: submitTask, loading: submitting } = useFetch(
    (body: TaskParams, _costCredits?: number) => {
      if (useV2ToolTaskApi) {
        return createTaskByToolV2({
          body,
        });
      }

      return createTaskByTool({
        body,
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error }, [body, costCredits]) => {
        const isNewbieTask = !!data?.extra_params?.matched_nexus_set;
        const reportEventParams = getReportParams
          ? getReportParams(isNewbieTask)
          : getDefaultReportParams({
              value: body,
              isInspireMe: false,
              isNewbieTask: isNewbieTask,
            });

        if (error) {
          handleError(error, reportEventParams);
          return;
        }

        if (data) {
          const mvMode = (body.settings as TaskParams['settings'] & { music_video?: { mode?: string } }).music_video
            ?.mode;
          const mvSceneMap: Record<
            'unspecified' | 'sing' | 'sing_perform' | 'dance' | 'perform',
            'sing' | 'sing_act' | 'dance' | 'performance' | undefined
          > = {
            unspecified: undefined,
            sing: 'sing',
            sing_perform: 'sing_act',
            dance: 'dance',
            perform: 'performance',
          };
          const mvScene = mvSceneMap[mvMode ?? 'unspecified'];
          if (useV2ToolTaskApi && mvScene) {
            saveOneClickMvTaskScene(data.id, mvScene);
          }

          // 处理积分更新

          updateCredits();
          handleDialogOpenChange(false);

          const reportSuccessEventParams = {
            ...reportEventParams,
            is_success: true,
            task_id: data.id,
          };

          // 上报点击提交按钮事件
          reportEvent(
            'create_video_click',
            taskType === 'upscale'
              ? {
                  ...reportSuccessEventParams,
                  interaction_position: pathname,
                }
              : reportSuccessEventParams,
          );

          if (taskType === 'video2extend' || taskType === 'video2extail') {
            reportEvent('extend_submit_click', {
              ...reportSuccessEventParams,
              extend_model_type: body.settings?.transition === 'extendspeed' ? '3.1_turbo' : '3.1_pro',
              with_last_frame: taskType === 'video2extail',
              interaction_position: pathname,
              consume_points: costCredits ?? 0,
              ratio: reportSuccessEventParams.resolution ?? '',
            });
          }

          // 这个 toast 可以被 onSubmitSuccess 的返回值控制
          // 返回 'suppressDefaultToast' 时不显示默认 toast
          const result = onSubmitSuccess?.(data);

          if (result !== 'suppressDefaultToast') {
            toast({
              message: t('popup.toast.taskSubmitSuccess'),
              type: 'success',
            });
          }
          // 在模版页面通过快速编辑提交的参考生图任务，不添加到任务历史列表
          const isTemplatesPage = pathname.match(/\/templates\/\w+/);
          const shouldSkipAddTask = isTemplatesPage && taskType === 'reference2image';
          if (!shouldSkipAddTask) {
            addNewTask(data);
          }

          // 上报任务排队中曝光事件（时机：排队中元素曝光时），如果虚拟列表没渲染 data-index="0" 的元素，则表示当前卡片没有曝光，不上报
          if (document.getElementById('taskHistoryContainer')?.querySelector('div[data-index="0"]')) {
            reportEvent('create_video_waiting', reportSuccessEventParams);
          }

          incrementPWAInstallCount();
          if (isFree) {
            // 免费用户记录当日发送任务次数，当日第二次时弹订阅
            recordTask();
          }
        }
      },
      onError: (error) => {
        const msg = getFetchErrorMsg(error, t);
        toast({
          type: 'error',
          message: msg,
        });
      },
    },
  );

  return {
    submitTask,
    submitting,
  };
};
